import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useHeader } from '../context/HeaderContext';
import api from '../services/api';
import { Play, Tv, Plus, Smartphone, Calendar, Clock, Activity, AlertCircle, X, ChevronRight } from 'lucide-react';
import './InteractiveSessions.css';

function InteractiveSessions() {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { setHeaderLeft } = useHeader();

    // Data lists
    const [sessions, setSessions] = useState([]);
    const [apps, setApps] = useState([]);
    const [builds, setBuilds] = useState([]);

    // Loading states
    const [loading, setLoading] = useState(true);
    const [appsLoading, setAppsLoading] = useState(false);
    const [buildsLoading, setBuildsLoading] = useState(false);
    const [startingSession, setStartingSession] = useState(false);

    // Dialog state
    const [showStartModal, setShowStartModal] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;

    // Form inputs for starting session
    const [selectedAppId, setSelectedAppId] = useState('');
    const [selectedBuildId, setSelectedBuildId] = useState('');
    const [deviceProfile, setDeviceProfile] = useState('PIXEL_7_PRO');
    const [idleTimeout, setIdleTimeout] = useState(10);
    const [error, setError] = useState('');

    // Set page header title
    useEffect(() => {
        if (setHeaderLeft) {
            setHeaderLeft(
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">Live Sessions</span>
                </div>
            );
        }
    }, [setHeaderLeft]);

    // 1. Load active/past sessions
    const loadSessions = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const response = await api.get('/interactive-sessions', {
                params: { page, limit }
            });
            if (response.data && response.data.sessions) {
                setSessions(response.data.sessions);
                setTotalPages(response.data.totalPages || 1);
            } else {
                setSessions([]);
                setTotalPages(1);
            }
        } catch (err) {
            console.error('Failed to load live sessions:', err);
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id, page]);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    // Handle pagination
    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    // 2. Load apps for start modal
    const loadApps = async () => {
        if (!currentWorkspace?.id) return;
        setAppsLoading(true);
        try {
            const response = await api.get('/apps', {
                params: {
                    workspaceId: currentWorkspace.id,
                    limit: 100
                }
            });
            setApps(response.data.rows || []);
        } catch (err) {
            console.error('Failed to load apps:', err);
        } finally {
            setAppsLoading(false);
        }
    };

    // 3. Load builds when app changes
    useEffect(() => {
        if (!selectedAppId) {
            setBuilds([]);
            setSelectedBuildId('');
            return;
        }

        const fetchBuilds = async () => {
            setBuildsLoading(true);
            try {
                const response = await api.get(`/apps/${selectedAppId}/builds`);
                setBuilds(response.data || []);
                if (response.data && response.data.length > 0) {
                    setSelectedBuildId(response.data[0].id); // select latest build by default
                }
            } catch (err) {
                console.error('Failed to fetch builds:', err);
            } finally {
                setBuildsLoading(false);
            }
        };

        fetchBuilds();
    }, [selectedAppId]);

    // Open start modal
    const handleOpenStartModal = () => {
        setError('');
        setSelectedAppId('');
        setSelectedBuildId('');
        setDeviceProfile('PIXEL_7_PRO');
        setIdleTimeout(10);
        setShowStartModal(true);
        loadApps();
    };

    // Form submit launcher
    const handleStartSessionSubmit = async (e) => {
        e.preventDefault();
        if (!selectedAppId || !selectedBuildId) {
            setError('Please select an App and build version to test.');
            return;
        }

        const appObj = apps.find(a => a.id === selectedAppId);
        if (!appObj) return;

        setStartingSession(true);
        setError('');
        try {
            const response = await api.post('/interactive-sessions/start', {
                appId: selectedAppId,
                platform: appObj.platform.toLowerCase(),
                deviceProfile,
                idleTimeoutMinutes: idleTimeout
            });
            setShowStartModal(false);
            navigate(`/interactive-session/${response.data.sessionId}`);
        } catch (err) {
            console.error('Failed to launch live session:', err);
            setError(err.response?.data?.error || 'Failed to start live session. Please try again.');
        } finally {
            setStartingSession(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="sessions-list-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Interactive Live Sessions</h1>
                    <p className="page-subtitle">
                        Provision live devices, view live emulators directly in your browser, record manual flows, and save them as AI test goals.
                    </p>
                </div>
                <button className="btn btn-primary flex items-center gap-1" onClick={handleOpenStartModal}>
                    <Plus size={16} /> Start Live Session
                </button>
            </div>

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading live sessions...</p>
                </div>
            ) : sessions.length === 0 ? (
                <div className="empty-sessions-card">
                    <Tv size={48} className="empty-icon animate-pulse" />
                    <h2>No Live Sessions Yet</h2>
                    <p>Launch a temporary cloud emulator instance to test your mobile app live, interact directly on the preview, and convert manual steps to test goals.</p>
                    <button className="btn btn-primary" onClick={handleOpenStartModal}>Start Your First Session</button>
                </div>
            ) : (
                <div className="sessions-list-container">
                    <table className="sessions-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>App & Platform</th>
                                <th>Date</th>
                                <th>Duration</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((sess) => {
                                const isRunning = sess.status === 'active' || sess.status === 'initializing';

                                // Format duration
                                let durationText = '-';
                                if (sess.durationSeconds != null) {
                                    const mins = Math.floor(sess.durationSeconds / 60);
                                    const secs = sess.durationSeconds % 60;
                                    durationText = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                                } else if (isRunning) {
                                    durationText = 'Running...';
                                }

                                return (
                                    <tr key={sess.id}>
                                        <td>
                                            <div className="session-status-badge flex items-center gap-1">
                                                {isRunning && <span className={`pulsing-dot ${sess.status}`}></span>}
                                                <span className={`status-text ${sess.status}`}>{sess.status}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="app-info-cell">
                                                <span className="app-name">{sess.app?.displayName || sess.app?.name || 'Mobile App Build'}</span>
                                                <span className="app-platform text-muted text-xs">
                                                    {sess.platform === 'android' ? '🤖 Android' : '🍎 iOS'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>{formatDate(sess.createdAt)}</td>
                                        <td>{durationText}</td>
                                        <td>
                                            <button
                                                className={`btn btn-sm ${isRunning ? 'btn-success' : 'btn-outline'} flex items-center gap-1`}
                                                onClick={() => navigate(`/interactive-session/${sess.id}`)}
                                            >
                                                {isRunning ? 'Join Session' : 'View Result'} <ChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="pagination-controls flex items-center justify-between mt-4">
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={handlePrevPage}
                                disabled={page === 1}
                            >
                                Previous
                            </button>
                            <span className="page-indicator text-sm text-muted">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Launch Modal */}
            {showStartModal && (
                <div className="modal-backdrop">
                    <div className="modal-content-card">
                        <div className="modal-header">
                            <h3>Start New Live Session</h3>
                            <button className="close-btn" onClick={() => setShowStartModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleStartSessionSubmit}>
                            <div className="modal-body">
                                {error && (
                                    <div className="modal-error-banner flex items-center gap-2">
                                        <AlertCircle size={16} />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Select App</label>
                                    {appsLoading ? (
                                        <div className="loading-dropdown">Loading apps list...</div>
                                    ) : (
                                        <select
                                            value={selectedAppId}
                                            onChange={(e) => setSelectedAppId(e.target.value)}
                                            required
                                        >
                                            <option value="">Choose an app...</option>
                                            {apps.map(app => (
                                                <option key={app.id} value={app.id}>
                                                    {app.displayName || app.name} ({app.platform})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Select Build Version</label>
                                    {buildsLoading ? (
                                        <div className="loading-dropdown">Loading builds history...</div>
                                    ) : (
                                        <select
                                            value={selectedBuildId}
                                            onChange={(e) => setSelectedBuildId(e.target.value)}
                                            disabled={!selectedAppId || builds.length === 0}
                                            required
                                        >
                                            <option value="">Choose version build...</option>
                                            {builds.map(build => (
                                                <option key={build.id} value={build.id}>
                                                    Version {build.version} {build.buildNumber ? `(Build ${build.buildNumber})` : ''} - {formatDate(build.createdAt)}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {selectedAppId && builds.length === 0 && !buildsLoading && (
                                        <p className="field-help error">No builds found. Please upload a build first.</p>
                                    )}
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Device Profile</label>
                                        <select
                                            value={deviceProfile}
                                            onChange={(e) => setDeviceProfile(e.target.value)}
                                        >
                                            <option value="PIXEL_7_PRO">Google Pixel 7 Pro</option>
                                            <option value="NEXUS_5X">Nexus 5X (Standard)</option>
                                            <option value="IPHONE_14_PRO">iPhone 14 Pro</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Idle Timeout (minutes)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="60"
                                            value={idleTimeout}
                                            onChange={(e) => setIdleTimeout(parseInt(e.target.value) || 10)}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowStartModal(false)}>Cancel</button>
                                <button
                                    type="submit"
                                    className="btn btn-primary flex items-center gap-1"
                                    disabled={startingSession || !selectedBuildId}
                                >
                                    {startingSession ? (
                                        <>Starting...</>
                                    ) : (
                                        <>
                                            <Play size={14} /> Launch Session
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InteractiveSessions;
